import { Loading } from '@affine/component';
import { FeatureUpgradeNudge } from '@affine/core/components/affine/feature-gate';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { OrganizeService } from '@affine/core/modules/organize';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  getFolderGrantedUsersListQuery,
  getMembersByWorkspaceIdQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { FolderIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from '../permissions/styles.css';
import {
  SettingsBadge,
  SettingsEntityAvatar,
  SettingsEntityIdentityAside,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
} from '../shared/settings-entity';
import { FolderPermissionsEditor } from './folder-permissions-editor';

interface OwnerInfo {
  id: string;
  name: string;
  avatarUrl: string | null;
}

interface FolderWithOwner {
  id: string;
  name: string;
  owner?: OwnerInfo;
}

interface FolderListViewProps {
  searchQuery: string;
}

/**
 * FolderListView - Shows folders from Y.js (OrganizeService).
 *
 * Architecture (from E035.3):
 * - Y.js = Source of Truth for folder STRUCTURE (names, hierarchy)
 * - PostgreSQL = Only for RBAC (permissions)
 *
 * Owner display: First checks folder_user_roles for explicit Owner,
 * falls back to workspace owner.
 */
export const FolderListView = ({ searchQuery }: FolderListViewProps) => {
  const t = useI18n();
  const organizeService = useService(OrganizeService);
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const isLoadingTree = useLiveData(folderTree.isLoading$);
  const children = useLiveData(rootFolder.sortedChildren$);
  const workspaceId = workspaceService.workspace.id;

  const [expandedFolderId, setExpandedFolderId] = useState<string | null>(null);
  const [folderOwners, setFolderOwners] = useState<Map<string, OwnerInfo>>(
    new Map()
  );
  const [workspaceOwner, setWorkspaceOwner] = useState<OwnerInfo | null>(null);

  // Get all folders from Y.js (OrganizeService)
  const allFolders = useMemo(() => {
    const folders: { id: string; name: string }[] = [];

    const collectFolders = (nodes: typeof children, _depth = 0) => {
      for (const node of nodes) {
        const type = node.type$.value;
        const name = node.name$.value;
        const id = node.id;

        if (type === 'folder' && id) {
          folders.push({
            id,
            name: name || 'Unnamed Folder',
          });
          // Recursively collect subfolders
          const nodeChildren = node.sortedChildren$.value;
          collectFolders(nodeChildren, _depth + 1);
        }
      }
    };

    collectFolders(children);
    return folders;
  }, [children]);

  // Load workspace owner (fallback for all folders)
  useEffect(() => {
    const loadWorkspaceOwner = async () => {
      if (!serverService.server) return;

      try {
        // Use getMembersByWorkspaceId to find owner
        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            skip: 0,
            take: 50,
          },
        });

        const members = (result as any).workspace?.members ?? [];
        const owner = members.find((m: any) => m.permission === 'Owner');
        if (owner) {
          setWorkspaceOwner({
            id: owner.id,
            name: owner.name || owner.email,
            avatarUrl: owner.avatarUrl ?? null,
          });
        }
      } catch (err) {
        console.debug('Failed to load workspace owner:', err);
      }
    };

    void loadWorkspaceOwner();
  }, [serverService.server, workspaceId]);

  // Load folder-specific owners from folder_user_roles
  useEffect(() => {
    const loadFolderOwners = async () => {
      if (!serverService.server || allFolders.length === 0) return;

      const owners = new Map<string, OwnerInfo>();

      for (const folder of allFolders) {
        try {
          const result = await serverService.server.gql({
            query: getFolderGrantedUsersListQuery,
            variables: {
              workspaceId,
              folderId: folder.id,
              pagination: { first: 10 },
            },
          });

          const items =
            (result as any).workspace?.folder?.grantedUsers?.items ?? [];
          const ownerGrant = items.find((item: any) => item.role === 'Owner');

          if (ownerGrant?.user) {
            owners.set(folder.id, {
              id: ownerGrant.user.id,
              name: ownerGrant.user.name || ownerGrant.user.email,
              avatarUrl: ownerGrant.user.avatarUrl ?? null,
            });
          }
        } catch {
          // No explicit owner for this folder
        }
      }

      setFolderOwners(owners);
    };

    void loadFolderOwners();
  }, [serverService.server, workspaceId, allFolders]);

  // Combine folders with owner info
  const foldersWithOwners = useMemo((): FolderWithOwner[] => {
    return allFolders.map(folder => ({
      ...folder,
      // Use explicit folder owner if exists, otherwise workspace owner
      owner: folderOwners.get(folder.id) || workspaceOwner || undefined,
    }));
  }, [allFolders, folderOwners, workspaceOwner]);

  // Filter folders by search query
  const filteredFolders = useMemo(() => {
    if (!searchQuery.trim()) return foldersWithOwners;
    const query = searchQuery.toLowerCase();
    return foldersWithOwners.filter(
      folder =>
        folder.name.toLowerCase().includes(query) ||
        folder.owner?.name.toLowerCase().includes(query)
    );
  }, [foldersWithOwners, searchQuery]);

  const handleFolderClick = useCallback((folderId: string) => {
    setExpandedFolderId(prev => (prev === folderId ? null : folderId));
  }, []);

  if (isLoadingTree) {
    return (
      <SettingsEntityMessage>
        <Loading size={20} />
        <span>
          {t['com.streamlain.settings.workspace.permissions.loading']?.() ??
            'Loading...'}
        </span>
      </SettingsEntityMessage>
    );
  }

  if (filteredFolders.length === 0) {
    return (
      <SettingsEntityMessage>
        {searchQuery
          ? (t[
              'com.streamlain.settings.workspace.folders.no-folders-found'
            ]?.() ?? 'No folders found matching your search.')
          : (t['com.streamlain.settings.workspace.folders.no-folders']?.() ??
            'No folders in this workspace.')}
      </SettingsEntityMessage>
    );
  }

  return (
    <SettingsEntityList>
      {filteredFolders.map(folder => (
        <FolderListItem
          key={folder.id}
          folder={folder}
          isExpanded={expandedFolderId === folder.id}
          onClick={() => handleFolderClick(folder.id)}
        />
      ))}
    </SettingsEntityList>
  );
};

interface FolderListItemProps {
  folder: FolderWithOwner;
  isExpanded: boolean;
  onClick: () => void;
}

const FolderListItem = ({
  folder,
  isExpanded,
  onClick,
}: FolderListItemProps) => {
  const t = useI18n();
  const { isFeatureAllowed } = useFeatureGate();
  const hasFolderRbac = isFeatureAllowed('folder-rbac');
  return (
    <SettingsExpandableItem
      expanded={isExpanded}
      onToggle={onClick}
      leading={<FolderIcon className={styles.listItemIcon} />}
      title={folder.name}
      badges={hasFolderRbac ? <SettingsBadge>RBAC</SettingsBadge> : undefined}
      aside={
        folder.owner ? (
          <SettingsEntityIdentityAside
            avatar={
              <SettingsEntityAvatar
                size={20}
                userId={folder.owner.id}
                name={folder.owner.name}
                url={folder.owner.avatarUrl}
              />
            }
            name={folder.owner.name}
          />
        ) : undefined
      }
    >
      {hasFolderRbac ? (
        <FolderPermissionsEditor folderId={folder.id} />
      ) : (
        <FeatureUpgradeNudge
          featureKey="folder-rbac"
          featureName={
            t['com.streamlain.settings.workspace.folders.title']?.() ??
            'Folder Permissions'
          }
          requiredPlan="Team"
        />
      )}
    </SettingsExpandableItem>
  );
};
